/**
 * FirmaBelgeSilModal.jsx — yüklenmiş bir firma belgesini silme onayı (backlog #736; backend #735).
 *
 * İki sayfa kullanır: Firma Kartı › Evraklar (firma kendi belgesini siler; yalnızca onaylanmamış — beklemede / reddedilmiş) ve personelin
 * Firma Profili İncele › Belgeler tablosu (herhangi bir firmanın belgesi; onaylı da silinebilir).
 *
 * Pencere silmenin çalışanların kartlarına etkisini söyler:
 *   · Onaylanmamış belge kartlara sayılmaz → "hiçbir kartı etkilemez".
 *   · ONAYLI belge: ilk "Devam" onaysız DELETE atar; backend 409 + `data.onayGerekli` ile uyarıyı (firmanın bu türdeki tek onaylı kopyası mı,
 *     başka onaylı kopya var mı) istek dilinde söyler, pencere o metni gösterir, "Yine de sil" `?confirm=true` ile tekrarlar.
 *     Silme ancak o ikinci onaydan sonra olur (onay vermeden hiçbir şey silinmez).
 * Başarıda `onDeleted(mesaj)` çağrılır; sayfa listeyi yeniden okur ve kutucuk yeniden yüklenmemiş duruma döner.
 *
 * Çağıran pencereyi `{belge && <FirmaBelgeSilModal key={belge.id} .../>}` olarak basar: her açılış temiz başlar.
 *
 * @param {number}   firmaId
 * @param {{ id: number, ad: string, approvalStatus: number }} belge  approvalStatus: 1 beklemede · 2 onaylı · 3 reddedilmiş (ham satır durumu)
 * @param {Function} onClose
 * @param {Function} onDeleted  (mesaj: string) => void
 */
import React, { useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Alert, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import { firmaService } from "../../services/firmaService";

export default function FirmaBelgeSilModal({ firmaId, belge, onClose, onDeleted }) {
  const { t } = useTranslation(["belgeSil", "common"]);
  const [asama, setAsama] = useState("sor"); // "sor" → (yalnızca onaylı belgede) "onay": sunucunun uyarısı gösterilir
  const [uyari, setUyari] = useState(null);
  const [siliniyor, setSiliniyor] = useState(false);
  const [hata, setHata] = useState(null);
  const onayli = belge.approvalStatus === 2;

  async function sil() {
    setSiliniyor(true);
    setHata(null);
    try {
      await firmaService.deleteFirmaDocument(firmaId, belge.id, { confirm: asama === "onay" });
      onDeleted(t("belgeSil:belgeSil.silindi", { ad: belge.ad }));
    } catch (e) {
      if (e.onayGerekli) {
        setUyari(e.message);
        setAsama("onay");
      } else {
        setHata(e.message);
      }
      setSiliniyor(false);
    }
  }

  const dugme = asama === "onay" ? t("belgeSil:belgeSil.yineDeSil") : onayli ? t("belgeSil:belgeSil.devam") : t("common:sil");

  return (
    <Modal isOpen toggle={siliniyor ? undefined : onClose} data-testid="belge-sil-penceresi">
      <ModalHeader toggle={siliniyor ? undefined : onClose}>{t("belgeSil:belgeSil.baslik")}</ModalHeader>
      <ModalBody>
        {asama === "onay" ? (
          <Alert color="warning" className="mb-0" data-testid="belge-sil-uyari">
            <strong>{t("belgeSil:belgeSil.onayliUyari")}.</strong>{" "}{uyari}
          </Alert>
        ) : (
          <>
            <p className="mb-2">{t(onayli ? "belgeSil:belgeSil.soruOnayli" : "belgeSil:belgeSil.soru", { ad: belge.ad })}</p>
            <p className="text-muted small mb-0">{t(onayli ? "belgeSil:belgeSil.onayliAdim" : "belgeSil:belgeSil.kartEtkisiYok")}</p>
          </>
        )}
        {hata && <Alert color="danger" className="mt-3 mb-0" data-testid="belge-sil-hata">{hata}</Alert>}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" disabled={siliniyor} onClick={onClose}>{t("common:iptal")}</Button>
        <Button color="danger" disabled={siliniyor} onClick={sil} data-testid="belge-sil-onayla">
          {siliniyor ? <Spinner size="sm" /> : dugme}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
